import React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, Trophy, BookOpen, Clock } from "lucide-react"
import { useAuth } from "../contexts/auth-context"
import { useTranslation } from "../contexts/translation-context"

interface GoogleLoginProps {
  onSuccess?: () => void
  showBenefits?: boolean
  title?: string
  description?: string
}

export function GoogleLogin({ onSuccess, showBenefits = true, title, description }: GoogleLoginProps) {
  const { t } = useTranslation()
  const { loginWithGoogle, isLoading, error, clearError, resetAuthState } = useAuth()
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  // Reset login state when component mounts or when error changes
  useEffect(() => {
    if (error) {
      // If there's an error, ensure we can retry
      setIsLoggingIn(false)
    }
  }, [error])

  const handleLogin = async () => {
    try {
      setIsLoggingIn(true)
      clearError()

      // Properly await the async function
      await loginWithGoogle()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      console.error("Login failed:", error)
      // Error is already handled in the auth context
      // Reset login state to allow retries
      setIsLoggingIn(false)
      resetAuthState()
    } finally {
      setIsLoggingIn(false)
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="text-2xl text-primary">{title || t("auth.signUpToSaveProgress")}</CardTitle>
        <CardDescription>{description || t("auth.connectWithGoogle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {showBenefits && (
          <div className="space-y-3 mb-6">
            <h4 className="font-semibold text-sm text-muted-foreground">{t("auth.whatYoullGet")}</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-3 text-sm">
                <Trophy className="w-4 h-4 text-secondary" />
                <span>{t("auth.saveQuizScores")}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <BookOpen className="w-4 h-4 text-primary" />
                <span>{t("auth.accessPastExams")}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Clock className="w-4 h-4 text-secondary" />
                <span>{t("auth.trackLearningHistory")}</span>
              </div>
            </div>
          </div>
        )}

        <Button
          onClick={handleLogin}
          disabled={isLoading || isLoggingIn}
          className="w-full bg-[#4285F4] hover:bg-[#3367D6] text-white"
          size="lg"
        >
          {isLoggingIn || isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t("auth.connecting")}
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t("auth.continueWithGoogle")}
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">{t("auth.dataSecure")}</p>
      </CardContent>
    </Card>
  )
}
